import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";

export default function IftarSummarizeNew() {
  const [loading, setLoading] = useState(false);
  const [loadingFinal, setLoadingFinal] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [speakingRow, setSpeakingRow] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isFinalMode, setIsFinalMode] = useState(false);

  const [aiLiveData, setAiLiveData] = useState(null);
  const [aiLiveLoading, setAiLiveLoading] = useState(false);
  const lastAiLiveRef = useRef(null);
  const [finalUsed, setFinalUsed] = useState(false);

  // ── State for AI Commentary TTS ──────────────────────────────────────
  const [speakingAiLive, setSpeakingAiLive] = useState(false);

  const speakingRowRef = useRef(null);
  const doneRef = useRef(false);

  const PORTAL = process.env.REACT_APP_PORTAL_DESTINATION;
  const PATH = process.env.REACT_APP_PATH_DESTINATION;

  const handleBannerToggle = async () => {
    const newVal = !bannerVisible;
    setBannerVisible(newVal);
    try {
      await axios.post(`${PATH}/iftarExchangeSetBanner`, { visible: newVal });
    } catch (err) {
      console.error("Banner toggle error:", err);
    }
  };

  // ── Helper: hide banner if visible ───────────────────────────────────
  const autoHideBanner = async () => {
    if (!bannerVisible) return;
    setBannerVisible(false);
    try {
      await axios.post(`${PATH}/iftarExchangeSetBanner`, { visible: false });
    } catch (err) {
      console.error("Auto-hide banner error:", err);
    }
  };

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`${PORTAL}getAILiveDB`);
        const d = res.data?.data;
        if (!d) return;
        if (
          lastAiLiveRef.current?.comment === d.comment &&
          lastAiLiveRef.current?.active === d.active
        ) return;
        lastAiLiveRef.current = d;
        if (d.active === 1 && d.comment) {
          setAiLiveData({ comment: d.comment, type: d.type || "insight" });
          setAiLiveLoading(false);
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [PORTAL]);

  const finishAndLoad = useCallback(async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    try {
      const summaryRes = await axios.get(`${PATH}/iftarExchangeGetSummarize`);
      if (Array.isArray(summaryRes.data) && summaryRes.data.length > 0) {
        setQuestions(summaryRes.data.map((row) => row.question));
      }
      setDone(true);
    } catch (err) {
      console.error("finishAndLoad error:", err);
      doneRef.current = false;
    } finally {
      setLoading(false);
      setLoadingFinal(false);
    }
  }, [PATH]);

  // Polling for status
  useEffect(() => {
    if (!loading && !loadingFinal) return;
    const interval = setInterval(async () => {
      if (doneRef.current) { clearInterval(interval); return; }
      try {
        const res = await axios.get(`${PATH}/iftarExchangeGetStatus`);
        const data = res.data;
        const currentStatus = Array.isArray(data) ? data[0]?.status : data?.status;
        if (Number(currentStatus) === 1) {
          clearInterval(interval);
          finishAndLoad();
        }
      } catch {}
    }, 800);
    return () => clearInterval(interval);
  }, [loading, loadingFinal, finishAndLoad, PATH]);

  // ── Generate & Summarize ──────────────────────────────────────────────
  const handleGenerate = async () => {
    doneRef.current = false;
    setLoading(true);
    setDone(false);
    setError("");
    setQuestions([]);
    setIsFinalMode(false);
    setAiLiveLoading(true);
    setAiLiveData(null);
    lastAiLiveRef.current = null;
    setSpeakingAiLive(false);

    // Auto-hide banner if visible
    await autoHideBanner();

    try {
      await axios.post(`${PATH}/iftarExchangeSetMode`, { mode: "generate" }).catch((e) =>
        console.warn("setMode generate error:", e)
      );

      await axios.post(`${PATH}/iftarExchangeUpdateStatusProcess`);
      await axios.post(`${PATH}/iftarExchangeUpdateSummarize`).catch((e) =>
        console.warn("iftarExchangeUpdateSummarize error:", e)
      );

      const dbRes = await axios.get(`${PATH}/iftarExchangeGetQuestion`);
      const rawData = dbRes.data;

      const [summarizeRes] = await Promise.all([
        axios.post(`${PATH}/iftarExchangeSummarize`, { rawData }),
        axios.get(`${PORTAL}ailiveshowcomment`).catch((e) =>
          console.warn("ailiveshowcomment error:", e)
        ),
      ]);

      const qs = summarizeRes.data.questions;
      if (!qs || !Array.isArray(qs)) throw new Error("Invalid response from summarizer.");

      await Promise.all(
        qs.map((q) =>
          axios.post(`${PATH}/iftarExchangeInsertSummarize`, { question: q })
        )
      );

      await axios.post(`${PATH}/iftarExchangeUpdateStatusDone`);
      await axios.post(`${PATH}/iftarExchangeSetRevealRow`, { revealCount: 0 }).catch(() => {});
      await axios.post(`${PATH}/iftarExchangeSetSpeakRow`, { rowIndex: null }).catch(() => {});
      await finishAndLoad();
      setFinalUsed(false);

    } catch (err) {
      console.error("handleGenerate error:", err);
      setTimeout(() => {
        if (!doneRef.current) {
          setError("Something went wrong. Please try again.");
          setLoading(false);
        }
      }, 3000);
      try { await axios.post(`${PATH}/iftarExchangeUpdateStatusDone`); } catch {}
    }
  };

  // ── Final Question ────────────────────────────────────────────────────
  const handleFinal = async () => {
    if (loading || loadingFinal) return;
    doneRef.current = false;
    setLoadingFinal(true);
    setDone(false);
    setError("");
    setQuestions([]);
    setIsFinalMode(true);
    setAiLiveLoading(true);
    setAiLiveData(null);
    lastAiLiveRef.current = null;
    setSpeakingAiLive(false);

    // Auto-hide banner if visible
    await autoHideBanner();

    try {
      await axios.post(`${PATH}/iftarExchangeSetMode`, { mode: "final" }).catch((e) =>
        console.warn("setMode final error:", e)
      );

      await axios.post(`${PATH}/iftarExchangeUpdateStatusProcess`);
      await axios.post(`${PATH}/iftarExchangeUpdateSummarize`).catch((e) =>
        console.warn("iftarExchangeUpdateSummarize error:", e)
      );

      const dbRes = await axios.get(`${PATH}/iftarExchangeGetQuestion`);
      const rawData = dbRes.data;

      const [summarizeRes] = await Promise.all([
        axios.post(`${PATH}/iftarExchangeSummarize`, { rawData }),
        axios.get(`${PORTAL}ailiveshowfinal`).catch((e) =>
          console.warn("ailiveshowfinal error:", e)
        ),
      ]);

      const qs = summarizeRes.data.questions;
      if (!qs || !Array.isArray(qs)) throw new Error("Invalid response from summarizer.");

      await Promise.all(
        qs.map((q) =>
          axios.post(`${PATH}/iftarExchangeInsertSummarize`, { question: q })
        )
      );

      await axios.post(`${PATH}/iftarExchangeUpdateStatusDone`);
      await axios.post(`${PATH}/iftarExchangeSetRevealRow`, { revealCount: 0 }).catch(() => {});
      await axios.post(`${PATH}/iftarExchangeSetSpeakRow`, { rowIndex: null }).catch(() => {});
      await finishAndLoad();
      setFinalUsed(true);

    } catch (err) {
      console.error("handleFinal error:", err);
      setTimeout(() => {
        if (!doneRef.current) {
          setError("Something went wrong. Please try again.");
          setLoadingFinal(false);
        }
      }, 3000);
      try { await axios.post(`${PATH}/iftarExchangeUpdateStatusDone`); } catch {}
    }
  };

  // ── Speak AI Commentary (rowIndex = -1) ───────────────────────────────
  const handleSpeakAiLive = async () => {
    if (speakingAiLive || !aiLiveData || speakingRow !== null) return;
    setSpeakingAiLive(true);
    try {
      await axios.post(`${PATH}/iftarExchangeSetSpeakRow`, { rowIndex: -1 });
    } catch (err) {
      console.error("SpeakAiLive error:", err);
      setSpeakingAiLive(false);
    }
  };

  const handleSpeak = async (index) => {
    if (speakingRow !== null) return;
    setSpeakingRow(index);
    speakingRowRef.current = index;
    try {
      await axios.post(`${PATH}/iftarExchangeSetSpeakRow`, { rowIndex: index });
    } catch (err) {
      console.error("Set speak row error:", err);
      setSpeakingRow(null);
      speakingRowRef.current = null;
    }
  };

  useEffect(() => {
    axios.post(`${PATH}/iftarExchangeSetSpeakRow`, { rowIndex: null }).catch(() => {});
  }, [PATH]);

  // ── Polling: speakRow — termasuk reset speakingAiLive ─────────────────
  useEffect(() => {
    if (questions.length === 0 && !speakingAiLive) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${PATH}/iftarExchangeGetSpeakRow`);
        const data = res.data;
        const rowIndex = Array.isArray(data) ? data[0]?.rowIndex : data?.rowIndex;
        const val = rowIndex !== undefined && rowIndex !== null ? Number(rowIndex) : null;
        speakingRowRef.current = val;
        setSpeakingRow(val);
        // Reset speakingAiLive saat Dashboard sudah selesai play (val kembali ke null)
        if (val !== -1) setSpeakingAiLive(false);
      } catch {}
    }, 600);
    return () => clearInterval(interval);
  }, [questions, speakingAiLive, PATH]);

  const isAnyLoading = loading || loadingFinal;

  return (
    <div style={{
      minHeight: "100dvh",
      background: "linear-gradient(180deg, #07111f 0%, #0a1628 60%, #061018 100%)",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      gap: "16px",
      padding: "28px 16px 56px",
      boxSizing: "border-box",
    }}>

      {/* ── Header ── */}
      <div style={{ textAlign: "center", marginBottom: "4px" }}>
        <p style={{
          margin: "0 0 5px",
          fontSize: "10px",
          letterSpacing: "3px",
          color: "rgba(255,255,255,0.3)",
          textTransform: "uppercase",
        }}>
          Iftar Exchange 2026
        </p>
        <h2 style={{
          margin: 0,
          fontSize: "clamp(17px, 5vw, 24px)",
          fontWeight: "700",
          color: "#e8c84a",
        }}>
          Presenter Control
        </h2>
      </div>

      {/* ── BUTTONS ── */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
        maxWidth: "480px",
      }}>

        {/* Button 1: Generate & Summarize */}
        <button
          onClick={handleGenerate}
          disabled={isAnyLoading}
          style={{
            width: "100%",
            padding: "16px 20px",
            background: loading
              ? "rgba(232,200,74,0.12)"
              : "linear-gradient(135deg, #e8c84a 0%, #f0a500 100%)",
            border: loading ? "1px solid rgba(232,200,74,0.4)" : "none",
            borderRadius: "10px",
            color: loading ? "#e8c84a" : "#07111f",
            fontWeight: "700",
            fontSize: "13px",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            cursor: isAnyLoading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            boxShadow: loading ? "none" : "0 4px 24px rgba(232,200,74,0.30)",
            transition: "all 0.2s ease",
            opacity: loadingFinal ? 0.45 : 1,
            minHeight: "52px",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {loading ? (
            <><span style={{ animation: "spin 1s linear infinite", display: "inline-block", fontSize: "16px" }}>⟳</span> Processing...</>
          ) : (
            <>✦ Generate &amp; Summarize</>
          )}
        </button>

        {/* Button 2: Final Question */}
        <button
          onClick={handleFinal}
          disabled={isAnyLoading || finalUsed}
          style={{
            width: "100%",
            padding: "16px 20px",
            background: loadingFinal
              ? "rgba(110,231,183,0.10)"
              : "linear-gradient(135deg, #6ee7b7 0%, #059669 100%)",
            border: loadingFinal ? "1px solid rgba(110,231,183,0.4)" : "none",
            borderRadius: "10px",
            color: loadingFinal ? "#6ee7b7" : "#07111f",
            fontWeight: "700",
            fontSize: "13px",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            cursor: isAnyLoading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            boxShadow: loadingFinal ? "none" : "0 4px 24px rgba(110,231,183,0.22)",
            transition: "all 0.2s ease",
            opacity: loading || finalUsed ? 0.45 : 1,
            minHeight: "52px",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {loadingFinal ? (
            <><span style={{ animation: "spin 1s linear infinite", display: "inline-block", fontSize: "16px" }}>⟳</span> Generating...</>
          ) : (
            <>◈ Final Question</>
          )}
        </button>

        {/* Button 3: Banner Toggle */}
        <button
          onClick={handleBannerToggle}
          style={{
            width: "100%",
            padding: "16px 20px",
            background: bannerVisible ? "rgba(100,200,255,0.12)" : "transparent",
            border: bannerVisible
              ? "1px solid rgba(100,200,255,0.55)"
              : "1px solid rgba(100,200,255,0.28)",
            borderRadius: "10px",
            color: bannerVisible ? "#64c8ff" : "rgba(100,200,255,0.55)",
            fontWeight: "700",
            fontSize: "13px",
            letterSpacing: "1.5px",
            textTransform: "uppercase",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            boxShadow: bannerVisible ? "0 4px 20px rgba(100,200,255,0.15)" : "none",
            transition: "all 0.2s ease",
            minHeight: "52px",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {bannerVisible ? "✕ Hide Banner" : "⬛ Show Banner"}
        </button>
      </div>

      {/* ── AI Live Analysis Box ── */}
      <div style={{
        width: "100%",
        maxWidth: "480px",
        borderRadius: "10px",
        border: speakingAiLive
          ? "1px solid rgba(110,231,183,0.35)"
          : "1px solid rgba(110,231,183,0.13)",
        background: speakingAiLive
          ? "rgba(110,231,183,0.04)"
          : "rgba(255,255,255,0.02)",
        overflow: "hidden",
        transition: "border 0.3s ease, background 0.3s ease",
      }}>
        <div style={{
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <span style={{
            fontSize: "9px",
            letterSpacing: "2.5px",
            color: "rgba(255,255,255,0.22)",
            textTransform: "uppercase",
          }}>
            AI Commentary · Top Box
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{
              fontSize: "9px",
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: aiLiveLoading ? "#e8c84a" : aiLiveData ? "#6ee7b7" : "rgba(255,255,255,0.15)",
            }}>
              {aiLiveLoading ? "⟳ Generating..." : speakingAiLive ? "🔊 Speaking..." : aiLiveData ? "● Live" : "○ Idle"}
            </span>

            {/* TTS Button for AI Commentary */}
            <button
              onClick={handleSpeakAiLive}
              disabled={speakingAiLive || !aiLiveData || speakingRow !== null}
              title={
                !aiLiveData ? "No AI commentary yet"
                : speakingRow !== null ? "Wait for current audio to finish"
                : speakingAiLive ? "Speaking..."
                : "Read AI commentary aloud on display"
              }
              style={{
                padding: "4px 10px",
                borderRadius: "6px",
                border: speakingAiLive
                  ? "1px solid rgba(110,231,183,0.55)"
                  : "1px solid rgba(110,231,183,0.22)",
                background: speakingAiLive
                  ? "rgba(110,231,183,0.12)"
                  : "rgba(110,231,183,0.05)",
                color: speakingAiLive ? "#6ee7b7" : "rgba(110,231,183,0.5)",
                fontSize: "14px",
                cursor: (speakingAiLive || !aiLiveData || speakingRow !== null) ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                opacity: (!aiLiveData || speakingRow !== null) ? 0.3 : 1,
                transition: "all 0.2s ease",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              {speakingAiLive
                ? <span style={{ animation: "pulse 1s ease-in-out infinite" }}>🔊</span>
                : <span>🔈</span>}
            </button>
          </div>
        </div>

        <div style={{
          padding: "14px",
          minHeight: "52px",
          display: "flex",
          alignItems: "center",
        }}>
          {aiLiveData ? (
            <div
              className="ailive-content"
              dangerouslySetInnerHTML={{ __html: aiLiveData.comment }}
              style={{
                fontSize: "13px",
                color: speakingAiLive ? "rgba(110,231,183,0.85)" : "rgba(255,255,255,0.72)",
                lineHeight: "1.55",
                width: "100%",
                transition: "color 0.3s ease",
              }}
            />
          ) : (
            <p style={{
              margin: 0,
              fontSize: "12px",
              color: "rgba(255,255,255,0.16)",
              fontStyle: "italic",
            }}>
              {aiLiveLoading
                ? "Waiting for AI response..."
                : "No data yet — click Generate or Final Question to populate."}
            </p>
          )}
        </div>
      </div>

      {/* ── Status Messages ── */}
      {error && (
        <div style={{
          width: "100%",
          maxWidth: "480px",
          fontSize: "13px",
          color: "#ff6b6b",
          background: "rgba(255,80,80,0.07)",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "1px solid rgba(255,80,80,0.22)",
          boxSizing: "border-box",
        }}>
          {error}
        </div>
      )}

      {done && !loading && !error && (
        <p style={{
          margin: 0,
          fontSize: "12px",
          color: "#6ee7b7",
          letterSpacing: "0.5px",
          textAlign: "center",
        }}>
          {isFinalMode
            ? "✓ Final question sent — displayed on screen automatically."
            : `✓ ${questions.length} questions ready — displayed on screen automatically.`}
        </p>
      )}

      {/* ── Discussion Questions — hanya tampil di generate mode ── */}
      {done && !loading && !isFinalMode && questions.length > 0 && (
        <div style={{
          width: "100%",
          maxWidth: "480px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          animation: "fadeIn 0.5s ease",
        }}>
          <p style={{
            margin: "0 0 2px",
            fontSize: "9px",
            letterSpacing: "2.5px",
            color: "rgba(255,255,255,0.22)",
            textTransform: "uppercase",
            textAlign: "center",
          }}>
            Room Temperature · Bottom Box · Tap 🔈 to read aloud
          </p>

          {questions.map((q, i) => {
            const isSpeaking = speakingRow === i;
            return (
              <div key={i} style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                <div style={{
                  flex: 1,
                  padding: "13px 14px",
                  background: isSpeaking
                    ? "rgba(232,200,74,0.07)"
                    : "rgba(10, 25, 50, 0.55)",
                  border: isSpeaking
                    ? "1px solid rgba(232,200,74,0.38)"
                    : "1px solid rgba(30, 60, 100, 0.55)",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                }}>
                  <div style={{
                    position: "absolute",
                    left: 0, top: 0, bottom: 0,
                    width: "3px",
                    background: isSpeaking
                      ? "linear-gradient(180deg, #f7d75a 0%, #e8a000 100%)"
                      : "rgba(255,255,255,0.06)",
                    borderRadius: "8px 0 0 8px",
                    boxShadow: isSpeaking ? "0 0 8px rgba(232,200,74,0.35)" : "none",
                    transition: "all 0.2s ease",
                  }} />
                  <span style={{
                    flex: 1,
                    fontSize: "13px",
                    lineHeight: "1.45",
                    paddingLeft: "6px",
                    color: isSpeaking
                      ? "rgba(232,200,74,0.9)"
                      : "rgba(255,255,255,0.55)",
                    transition: "color 0.2s",
                  }}>
                    {q}
                  </span>
                  {isSpeaking && (
                    <div style={{
                      display: "flex",
                      gap: "2px",
                      alignItems: "center",
                      flexShrink: 0,
                    }}>
                      {[0,1,2,3,4].map(j => (
                        <div key={j} style={{
                          width: "3px",
                          background: "#e8c84a",
                          borderRadius: "2px",
                          animation: `waveBar 0.7s ease-in-out ${j * 0.07}s infinite alternate`,
                        }} />
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleSpeak(i)}
                  disabled={speakingRow !== null || speakingAiLive}
                  title={isSpeaking ? "Speaking..." : "Read aloud"}
                  style={{
                    flexShrink: 0,
                    width: "52px",
                    minHeight: "52px",
                    borderRadius: "8px",
                    border: isSpeaking
                      ? "1px solid rgba(232,200,74,0.55)"
                      : "1px solid rgba(232,200,74,0.22)",
                    background: isSpeaking
                      ? "rgba(232,200,74,0.13)"
                      : "rgba(232,200,74,0.05)",
                    cursor: (speakingRow !== null || speakingAiLive) ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    transition: "all 0.2s ease",
                    opacity: (speakingRow !== null && !isSpeaking) || speakingAiLive ? 0.35 : 1,
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  {isSpeaking
                    ? <span style={{ animation: "pulse 1s ease-in-out infinite" }}>🔊</span>
                    : <span>🔈</span>}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        @keyframes waveBar {
          from { height: 4px; }
          to { height: 18px; }
        }
        .ailive-content div,
        .ailive-content span,
        .ailive-content p {
          background-color: transparent !important;
          background: transparent !important;
          padding: 0 !important;
          margin: 0 !important;
          color: inherit !important;
          font-family: inherit !important;
          font-size: inherit !important;
          line-height: inherit !important;
        }
      `}</style>
    </div>
  );
}